import type { SoundtrackSceneMap } from "@thehoneyjar/sound-engine";

export const INTERFACE_SOUNDTRACK_SCENES: SoundtrackSceneMap = {
  // Lobby + every sunset room run the same after-hours atrium track.
  // Operator (session-3): "It'll be the only song that we'll have."
  // Suno-generated 2026-04-25 from THE SPEAKERS v4 prompt — Boards of
  // Canada Geogaddi meets Haruomi Hosono Watering a Flower, mall food
  // court PA reverb, found demo cassette 1999.
  "/": { track: "lobbyMusic" },
  "/lobby": { track: "lobbyMusic" },
  "/wall": { track: "lobbyMusic" },
  "/incinerator": { track: "lobbyMusic" },
  "/gallery": { track: "lobbyMusic" },
  "/vault": { track: "lobbyMusic" },
  "/lock": { track: "lobbyMusic" },
  "/mememaker": { track: "lobbyMusic" },
  // Only /buy keeps its own scene — surface stays alive.
  "/buy": { track: "buyHenloAmbient" },
};

const SCENE_KEYS = Object.keys(INTERFACE_SOUNDTRACK_SCENES).sort((a, b) => b.length - a.length);

export const resolveInterfaceScene = (pathname: string | null | undefined): string | null => {
  if (!pathname) return null;
  const match = SCENE_KEYS.find((key) => pathname === key || pathname.startsWith(`${key}/`));
  return match ?? null;
};

// ─── Audio Register: Hibernation Gate ────────────────────────────────────────
// Schema: route → audio register, derived from INTERFACE_SOUNDTRACK_SCENES.
//
// What: HIBERNATING_ROUTES is the set of routes whose track is lobbyMusic
// (the hibernation register). isRouteHibernating() is the canonical gate.
//
// Why: Sound is a register. Visual hibernation (frozen UI, reduced interactivity)
// requires an audio counterpart — no SFX should fire on a hibernated surface.
// A single derived source prevents flag drift between visual and audio state.
//
// How to extend:
//   • Add a route to INTERFACE_SOUNDTRACK_SCENES with track: lobbyMusic
//     and HIBERNATING_ROUTES updates automatically.
//   • New SFX consumers: wrap playSfx calls with
//     if (!isRouteHibernating(pathname)) { playSfx(...) }
//
// Canonical consumer: components/piggy-bank-easter-egg.tsx
// ─────────────────────────────────────────────────────────────────────────────

const HIBERNATION_TRACK = "lobbyMusic";

export const HIBERNATING_ROUTES: ReadonlySet<string> = new Set(
  Object.entries(INTERFACE_SOUNDTRACK_SCENES)
    .filter(([, scene]) => scene.track === HIBERNATION_TRACK)
    .map(([route]) => route),
);

// Sorted longest-first so /lock/foo inherits /lock's register, matching
// the scene-resolution algorithm at SCENE_KEYS above.
const SORTED_HIBERNATING_KEYS = [...HIBERNATING_ROUTES].sort(
  (a, b) => b.length - a.length,
);

export function isRouteHibernating(pathname: string): boolean {
  return SORTED_HIBERNATING_KEYS.some(
    (key) => pathname === key || pathname.startsWith(`${key}/`),
  );
}
